import React from "react";
import { useTranslation } from "react-i18next";
import { Draggable } from "@hello-pangea/dnd";
import { Icon } from "@/components/Icon";
import { Tooltip } from "@/components/Tooltip";
import { Badge } from "@/components/Badge";
import { PRIORITY_CONFIG } from "@/components/PrioritySelect";

export interface TaskCardTask {
    id: number;
    code: string;
    title: string;
    priority?: number | string;
    messages?: any[];
    sprint?: {
        id: number;
        name: string;
        [key: string]: any;
    } | null;
    assignee?: {
        id?: number;
        name: string;
        avatar?: string;
        photo?: string;
    } | null;
    [key: string]: any;
}

export interface TaskCardProps {
    task: TaskCardTask;
    index: number;
    onClick: (task: TaskCardTask) => void;
    onPriorityClick?: (task: TaskCardTask) => void;
    onAssigneeClick?: (task: TaskCardTask) => void;
    onSprintClick?: (task: TaskCardTask) => void;
    columnColor?: string;
}

const getInitials = (name?: string): string => {
    if (!name) {
        return "??";
    }

    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return name.substring(0, 2).toUpperCase();
};

export const TaskCard = ({
    task,
    index,
    onClick,
    onPriorityClick,
    onAssigneeClick,
    onSprintClick,
    columnColor,
}: TaskCardProps) => {
    const { t } = useTranslation("global");
    const priorityKey = task.priority ?? 2;
    const priorityCfg = PRIORITY_CONFIG[priorityKey] || PRIORITY_CONFIG[2];

    return (
        <Draggable draggableId={String(task.id)} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={() => onClick(task)}
                    onMouseEnter={(e) => {
                        const targetColor = columnColor || "#94a3b8";
                        if (!snapshot.isDragging) {
                            e.currentTarget.style.borderColor = targetColor;
                            e.currentTarget.style.boxShadow = `0 4px 14px -2px ${targetColor}30`;
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!snapshot.isDragging) {
                            e.currentTarget.style.borderColor = "";
                            e.currentTarget.style.boxShadow = "";
                        }
                    }}
                    className={`bg-white dark:bg-zinc-800 p-3.5 mb-3 rounded-xl shadow-xs border ${
                        snapshot.isDragging
                            ? "border-primary ring-2 ring-primary/20 shadow-md transform -translate-y-1 cursor-grabbing"
                            : "border-slate-200 dark:border-zinc-700 hover:shadow-md hover:bg-slate-50/50 dark:hover:bg-zinc-700/50 cursor-pointer"
                    } transition-all`}
                >
                    {/* Top: Title edge to edge with task code */}
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug w-full mb-2 break-words">
                        {task.code ? `${task.code} - ` : ""}
                        {task.title}
                    </h4>

                    {/* Bottom: Left (Sprint Badge & Messages), Right (Priority & User Avatar) */}
                    <div className="flex items-center justify-between gap-2">
                        {/* Left: Sprint Badge & Messages */}
                        <div className="flex items-center gap-2 min-w-0">
                            {/*
                            <Badge
                                variant="neutral"                                
                                value={task.sprint?.name || t('tasks.sprints.backlog')}
                                onClick={onSprintClick ? (e) => {
                                    e.stopPropagation();
                                    onSprintClick(task);
                                } : undefined}
                                className={onSprintClick ? 'cursor-pointer hover:border-primary/50 hover:text-primary transition-colors' : ''}
                            />
                            */}
                            {task.messages && task.messages.length > 0 && (
                                <div className="flex items-center gap-1 text-sm text-slate-400 dark:text-zinc-500 shrink-0 font-medium">
                                    <Icon value="message" size={16} />
                                    <span>{task.messages.length}</span>
                                </div>
                            )}
                        </div>

                        {/* Right: Priority & Assignee Avatar */}
                        <div className="flex items-center gap-2 shrink-0">
                            {/* Priority button */}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onPriorityClick) {
                                        onPriorityClick(task);
                                    }
                                }}
                                className="p-1 rounded-lg transition-transform hover:scale-105 cursor-pointer"
                            >
                                <Tooltip
                                    title={t(`priorities.${priorityCfg.tag}`)}
                                    placement="top"
                                >
                                    <span
                                        className={`inline-flex items-center -space-x-0.5 ${priorityCfg.color}`}
                                    >
                                        {Array.from({
                                            length: priorityCfg.arrows,
                                        }).map((_, idx) => (
                                            <Icon
                                                key={idx}
                                                name="arrowUp"
                                                size={16}
                                                strokeWidth={3}
                                            />
                                        ))}
                                    </span>
                                </Tooltip>
                            </button>

                            {/* Assignee Avatar button */}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onAssigneeClick) {
                                        onAssigneeClick(task);
                                    }
                                }}
                                className="cursor-pointer rounded-full transition-transform hover:scale-110 hover:ring-2 hover:ring-primary/40"
                            >
                                {task.assignee ? (
                                    <Tooltip
                                        title={task.assignee.name}
                                        placement="top"
                                    >
                                        {task.assignee.avatar ||
                                        task.assignee.photo ? (
                                            <img
                                                src={
                                                    task.assignee.avatar ||
                                                    task.assignee.photo
                                                }
                                                alt={task.assignee.name}
                                                className="w-7 h-7 rounded-full object-cover shrink-0 ring-1 ring-slate-200 dark:ring-zinc-700"
                                            />
                                        ) : (
                                            <div className="avatar">
                                                {getInitials(
                                                    task.assignee.name
                                                )}
                                            </div>
                                        )}
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        title={t("tasks.table.unassigned")}
                                        placement="top"
                                    >
                                        <div className="avatar-unassigned">
                                            <Icon name="user" size={12} />
                                        </div>
                                    </Tooltip>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    );
};
